import { useContext, useEffect, useRef } from 'react'
import { CommonActions, useNavigation } from '@react-navigation/native'
import { StackNavigationProp } from '@react-navigation/stack'
import { useDispatch } from 'react-redux'
import { RootStackParamList } from '@src/navigation/types'
import { BillingProduct, EhrAppointmentKind, Product } from '@src/types.ts'
import { usePurchaseOneTimePayment } from '@screens/Marketplace/utils/hooks.ts'
import { NavigatorContext, useDispatchAsync, useSnack } from '@src/utils'
import { ErrorMessages } from '@src/config'
import Bugsnag from '@config/bugsnag'
import Storage from '@utils/storage.ts'
import { NavigationRouteConfig } from '@utils/analytics.ts'

export const useChargeFee = ({
  videoCallId,
  feeProduct,
  appointmentKind,
  selectedSlotDate,
}: {
  videoCallId: string
  feeProduct: Product | undefined
  appointmentKind?: EhrAppointmentKind
  selectedSlotDate?: Date
}) => {
  const navigation = useNavigation<StackNavigationProp<RootStackParamList>>()
  const { purchaseOneTimePayment } = usePurchaseOneTimePayment()
  const dispatchAsync = useDispatchAsync()
  const dispatch = useDispatch()
  const showSnack = useSnack()
  const { enableLoader, disableLoader } = useContext(NavigatorContext)
  const idempotencyKey = `${Storage.VIDEO_CALL_FEE_IDEMPOTENCY_KEY}_${videoCallId}`
  const cancellingRef = useRef(false)
  const reschedulingRef = useRef(false)

  useEffect(
    () => () => {
      if (cancellingRef.current || reschedulingRef.current) {
        disableLoader()
        cancellingRef.current = false
        reschedulingRef.current = false
      }
    },
    [disableLoader],
  )

  const navigateToNutritionistHub = (topRoute?: NavigationRouteConfig) => {
    navigation.dispatch((state) => {
      // Remove current route
      const routes = state.routes.slice(0, -1)

      // navigate to NutritionistHub
      const newRoutes = [
        ...routes,
        {
          name: 'Drawer',
          params: { screen: 'Dashboard', params: { screen: 'NutritionistHub' } },
        },
      ]

      if (topRoute) {
        newRoutes.push(topRoute)
      }

      return CommonActions.reset({
        index: newRoutes.length - 1,
        routes: newRoutes,
      } as any)
    })
  }

  const onSuccess = async () => {
    if (feeProduct?.key === BillingProduct.VideoCallLateCancellationFee) {
      if (cancellingRef.current) {
        return
      }

      cancellingRef.current = true
      enableLoader()

      try {
        await dispatchAsync({
          type: 'nutritionistHub/cancelAppointment',
          payload: {
            id: videoCallId,
          },
        })

        navigateToNutritionistHub({ name: 'FeePaymentSuccessfulModal' } as NavigationRouteConfig)
      } catch (error: any) {
        // The fee was charged successfully but cancelEhrAppointment failed.
        // The most common cause is a race where Stripe's `invoice.paid`
        // webhook hasn't propagated `paid_at` yet, so the server's
        // validator sees the fee as unpaid and raises
        // AppointmentLateCancellationFeeError. The backend schedules an
        // idempotent `LateCancelFromFeeJob` off of the `invoice.paid`
        // webhook that will cancel the appointment shortly, so we can
        // safely treat this as success for the user. Report to Bugsnag
        // so we can monitor the race rate and catch any non-race failures.
        Bugsnag.notify(
          error instanceof Error ? error : new Error(String(error?.message || error)),
          (event) => {
            event.addMetadata('cancelAfterFee', {
              videoCallId,
              feeProductKey: feeProduct.key,
              message: error?.message,
            })
            // eslint-disable-next-line no-param-reassign
            event.severity = 'warning'
          },
        )

        navigateToNutritionistHub({ name: 'FeePaymentSuccessfulModal' } as NavigationRouteConfig)
      } finally {
        disableLoader()
        cancellingRef.current = false
      }
    } else if (feeProduct?.key === BillingProduct.VideoCallLateRescheduleFee) {
      if (reschedulingRef.current) {
        return
      }

      reschedulingRef.current = true
      enableLoader()

      dispatch({
        type: 'nutritionistHub/rescheduleAppointment',
        payload: {
          date: selectedSlotDate,
          id: videoCallId,
        },
        success: () => {
          navigateToNutritionistHub()
          showSnack('Appointment rescheduled successfully', null, 'success')
        },
        failure: (error: any) => {
          dispatch({ type: 'nutritionistHub/fetchUpcomingEhrAppointments' })
          const errorMessage =
            error?.message ||
            'Something went wrong while rescheduling the appointment. Please try again.'
          navigateToNutritionistHub()
          showSnack(errorMessage, null, 'error')
        },
        complete: () => {
          disableLoader()
          reschedulingRef.current = false
        },
      })
    }
  }

  const chargeFee = async () => {
    if (!feeProduct) {
      return
    }

    const metadata = [{ key: 'appointment_id', value: videoCallId }]

    try {
      await purchaseOneTimePayment({
        product: feeProduct,
        quantity: 1,
        metadata,
        idempotencyKey,
      })

      await onSuccess()
    } catch (error: any) {
      showSnack(error?.message || ErrorMessages.ServerError, null, 'error')

      navigation.replace('PaymentFailed', {
        videoCallId,
        feeProduct,
        appointmentKind,
      })
    }
  }

  return chargeFee
}
